import * as React from "react";
import {
    HelpContainer,
    HelpButtonStyle,
    HelpMenuStyle,
    HelpMenuItemStyle,
    HelpMenuItemTitleStyle,
    HelpMenuSeparator,
} from "./help-button-style";

import * as userActions from "actions/user-actions";

import { isOSThemeDark, unwatchOSThemeDark, watchOSThemeDark } from "sharedUI/lib/color-theme-utils";
import ClickOutsideContainer from "sharedUI/ui/smart/click-outside-container/click-outside-container";
import { connect } from "react-redux";
import { RootState } from "store/types";
import { getUserFlags } from "common/Database";
import { trackEvent } from "analytics";
import { MappedThunk } from "actions/action-thunk";
import { useLocalStorage } from "hooks/use-local-storage";

import AnnounceKit from "announcekit-react";
import AppIcon from "common/app-icon";
import { LearningCenter } from "designer/apps/ui/smart/learning-center/learning-center";

interface HelpItem {
    title: string;
    isPro?: boolean;
    onClick(): void;
}

type MenuSeparator = null;
const menuSeparator: MenuSeparator = null;

const openLink = (link: string) => () => window.open(link, "_blank");

interface HelpMenuProps {
    items: Array<HelpItem | MenuSeparator>;
    onItemClicked(item: HelpItem): void;
    isPro: boolean;
}

function HelpMenu({ onItemClicked, items, isPro: currentUserHasProApps }: HelpMenuProps) {
    return (
        <HelpMenuStyle>
            {items.map((i, index) =>
                i === menuSeparator ? (
                    <HelpMenuSeparator key={index} />
                ) : i.isPro === true && !currentUserHasProApps ? null : (
                    <HelpMenuItem key={i.title} item={i} onClick={onItemClicked} />
                )
            )}
        </HelpMenuStyle>
    );
}

interface HelpMenuItemProps {
    item: HelpItem;
    onClick(item: HelpItem): void;
}

function HelpMenuItem({ item, onClick }: HelpMenuItemProps) {
    const { title, onClick: onClickItem } = item;
    return (
        <HelpMenuItemStyle
            onClick={() => {
                onClick(item);
                onClickItem();
            }}>
            <HelpMenuItemTitleStyle>{title}</HelpMenuItemTitleStyle>
        </HelpMenuItemStyle>
    );
}

interface HelpButtonProps {
    theme: "dark" | "light";
    isSystemTheme: boolean;
    setBuilderTheme: MappedThunk<typeof userActions.setBuilderTheme>;
    isPro: boolean;
    builderTrackingOptOut: boolean;
}

const HelpButtonInner: React.FC<HelpButtonProps> = p => {
    const { isPro, isSystemTheme, setBuilderTheme, theme, builderTrackingOptOut } = p;

    const [unreadUpdates, setUnreadUpdates] = React.useState<"loading" | number>("loading");
    const [showMenu, setShowMenu] = React.useState(false);
    const [highContrast, setHighContrast] = useLocalStorage("high-contrast-glide", false);
    const [isLearningCenterVisible, setIsLearningCenterVisible] = React.useState(false);

    React.useEffect(() => {
        function updateBuilderThemeOnSystemChange(isDark: boolean) {
            void setBuilderTheme(isDark ? "dark" : "light", true, false);
        }

        watchOSThemeDark(updateBuilderThemeOnSystemChange);

        return () => {
            unwatchOSThemeDark(updateBuilderThemeOnSystemChange);
        };
    }, [setBuilderTheme]);

    const onClose = React.useCallback(() => setShowMenu(false), []);

    const helpItemClicked = React.useCallback((item: HelpItem) => {
        setShowMenu(false);
        trackEvent("help menu item", { label: item.title });
    }, []);

    const onLearningCenterClose = React.useCallback(() => {
        setIsLearningCenterVisible(false);
    }, []);

    let isDark = theme === "dark";
    if (isSystemTheme) {
        isDark = isOSThemeDark();
    }

    const helpItems: Array<HelpItem | MenuSeparator> = [
        {
            title: "What's New",
            onClick: openLink("https://www.glideapps.com/updates"),
        },
        {
            title: "Learn to Glide",
            onClick: () => setIsLearningCenterVisible(c => !c),
        },
        {
            title: "Community",
            onClick: openLink("https://community.glideapps.com/"),
        },
        {
            title: "Documentation",
            onClick: openLink("https://docs.glideapps.com/"),
        },
        {
            title: "Support",
            onClick: openLink("https://www.glideapps.com/support"),
        },
        menuSeparator,
        {
            title: highContrast ? "Normal Contrast" : "High Contrast",
            onClick: () => setHighContrast(!highContrast),
        },
        {
            title: isDark ? "Light Mode" : "Dark Mode",
            onClick: async () => {
                const newTheme = isDark ? "light" : "dark";
                const osIsDark = isOSThemeDark();
                const isSystem = (newTheme === "dark" && osIsDark) || (newTheme === "light" && !osIsDark);
                await setBuilderTheme(newTheme, isSystem, true);
            },
        },
    ];

    const HelpButton = () => (
        <HelpButtonStyle
            onClick={() => {
                const s = !showMenu;
                if (s) {
                    trackEvent("help button", {});
                }
                setShowMenu(s);
            }}>
            ?
        </HelpButtonStyle>
    );

    const AnnouncementsButton = () => (
        <AnnounceKit
            widget="https://announcekit.app/widgets/v2/2mxpTO"
            onWidgetUnread={(unread: number) => {
                setUnreadUpdates(unread);
            }}
            onWidgetClose={() => {
                setUnreadUpdates(0);
            }}
            widgetStyle={{
                position: "absolute",
                top: 0,
                right: 0,
            }}>
            <HelpButtonStyle>
                {unreadUpdates === "loading" ? undefined : <AppIcon icon="01-20-alarm-bell-1" size={18} />}
            </HelpButtonStyle>
        </AnnounceKit>
    );

    const shouldShowUpdates = unreadUpdates === "loading" || unreadUpdates > 0;

    return (
        <ClickOutsideContainer style={{ height: "100%" }} onClickOutside={onClose}>
            <HelpContainer>
                {showMenu && <HelpMenu items={helpItems} isPro={isPro} onItemClicked={helpItemClicked} />}
                {!builderTrackingOptOut && shouldShowUpdates ? <AnnouncementsButton /> : <HelpButton />}
            </HelpContainer>
            <LearningCenter show={isLearningCenterVisible} onClose={onLearningCenterClose} />
        </ClickOutsideContainer>
    );
};

const mapState = (state: RootState) => ({
    theme: getUserFlags(state.userData).builderTheme,
    builderTrackingOptOut: getUserFlags(state.userData).builderTrackingOptOut,
    isSystemTheme: getUserFlags(state.userData).useSystemTheme,
    isPro: state.userData !== "loading" && state.userData !== null ? state.userData.planCounts.pro > 0 : false,
});

const mapDispatch = {
    setBuilderTheme: userActions.setBuilderTheme,
};

export const HelpButton = connect(mapState, mapDispatch)(HelpButtonInner);
